import { Injectable } from '@angular/core';
import { BlobServiceClient, ContainerClient} from '@azure/storage-blob';
import {HttpClient} from '@angular/common/http';
import {Observable} from 'rxjs/Observable';
import {Subject, of} from 'rxjs';
import {BlobModel} from '../../models/blob/blob.model';
import {BlobTransferModel} from '../../models/blob/blob-transfer.model';
import {MatDialog, MatDialogConfig} from '@angular/material/dialog';
import {UploadDialogComponent} from '../../dialog/upload-dialog/upload-dialog.component';

(window as any).global = window;
(window as any).process = require( 'process' );
(window as any).Buffer = require( 'buffer' ).Buffer;

@Injectable({
  providedIn: 'root'
})
export class AzureBlobStorageService {
  accountName: string = ''; // This should be dynamic in the future
  mainContainerName: string = 'tdox';
  // mainContainerName: string = 'angulartest';
  _sas: string = '';
  numRetries = 2;
  attempts = 0;

  isUploading = false;
  uploadProgress: Subject<BlobTransferModel> = new Subject<BlobTransferModel>();
  abortControllers: AbortController[] = [];

  //private _client: BlobServiceClient = null;

  constructor(private http: HttpClient,
              private dialog: MatDialog) {
  }

  // Calls to backend
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  getAccountName() {
    if (typeof this.accountName === "string" && this.accountName.length > 0)
      return of(this.accountName);
    let subdomain = window.location.hostname;
    if (subdomain.includes('.')) {
      subdomain = subdomain.split('.')[0];
    }
    if (subdomain === 'localhost') {
      subdomain = 'localhost:4200';
    }
    return this.http.post<string>(`api/azure/get-acc-name`, {subdomain});
  }

  getSas() {
    let subdomain = window.location.hostname;
    if (subdomain.includes('.')) {
      subdomain = subdomain.split('.')[0];
    }
    if (subdomain === 'localhost') {
      subdomain = 'localhost:4200';
    }
    return this.http.post<string>(`api/azure/get-sas`, {subdomain});
  }
  // End Calls to backend
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  // Calls to Azure
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
   * listBlobs, will list all files in the selected container.
   */
  async listBlobs(handler: (results: string[]) => void) {
    const result: string[] = [];
    (await this.getServiceClient()).subscribe(async (serviceClient) => {
      const containerClient = serviceClient.getContainerClient(this.mainContainerName);
      const ivl = setInterval(async () => {
        if (!containerClient) {
          return;
        }
        clearInterval(ivl);
        try {
          const blobs = serviceClient.getContainerClient(this.mainContainerName).listBlobsFlat();
          if (blobs) {
            for await (const blob of blobs) {
              result.push(blob.name);
            }
          }
          handler(result);
        } catch (e: any) {
          console.log('Error getting container: ', e);
          this._sas = '';
          this.listBlobs(handler);
        }
      }, 100);
    });

  }

  async uploadBlobs(content: BlobModel[], handler: (isSuccess: boolean) => void) {
    let totalSize = 0;
    let transfered = 0;
    let numItemsTransfered = 0;
    let avgTimeBetweenProgress = 0;
    const startTime = new Date().getTime();
    content.forEach((blob) => totalSize += blob.content.size);
    if (content.length > 0) {
      this.isUploading = true;
      const config = new MatDialogConfig();
      config.panelClass = 'dialog';
      this.dialog.open(UploadDialogComponent, config);
    }

    (await this.getServiceClient()).subscribe((serviceClient) => {
      content.forEach(async (blob) => {
        const abortController = new AbortController();
        this.abortControllers.push(abortController);

        let i = 0;
        let itemBytesLoaded = 0;
        let lastTimeDiff = 0;
        let path = '';
        if (blob.patientId) {
          path = `patient/${blob.patientId}/${blob.name}`;
        } else {
          path = `other/${blob.name}`;
        }
        const blockBlobClient = serviceClient.getContainerClient(this.mainContainerName).getBlockBlobClient(path);
        await blockBlobClient.uploadData(blob.content, {
          abortSignal: abortController.signal,
          blockSize: 4 * 1024 * 1024, // 4MB block size
          concurrency: 20, // 20 concurrency
          onProgress: (ev) => {
            i++;
            const currentTime = new Date().getTime();
            const diff = ev.loadedBytes - itemBytesLoaded;
            itemBytesLoaded = ev.loadedBytes;
            transfered += diff;
            const xferModel = new BlobTransferModel();
            xferModel.transfered = transfered;
            xferModel.total = totalSize;
            xferModel.progress = Math.round((transfered / totalSize) * 100);

            xferModel.durationMs = currentTime - startTime;

            lastTimeDiff = currentTime - startTime - lastTimeDiff;

            avgTimeBetweenProgress += lastTimeDiff / i;
            const remainingProgress = (100 / xferModel.progress) * avgTimeBetweenProgress;
            xferModel.estimatedTimeLeftMs = (100 - xferModel.progress) * xferModel.durationMs;
            this.uploadProgress.next(xferModel);
          }, blobHTTPHeaders: {blobContentType: blob.content.type}
        }).then(response => {
          numItemsTransfered++;
          if (numItemsTransfered === content.length) {
            this.isUploading = false;
            this.abortControllers = [];
            handler(true);
          }
        }).catch((error) => {
          handler(false);
          // Aborted?
        });
      });
    });
  }

  /**
   * downloadBlob - Download a blob item by name, in a known container.
   * @param filename - Name of file to retrieve
   * @param handler - Name of function to call after
   * @param containerName - Name of container to look in (optional)
   */
  async downloadBlob(filename: string, handler: (blob: Blob) => void, containerName: string = null) {
    if (!containerName) {
      containerName = this.mainContainerName;
    }
    (await this.getServiceClient()).subscribe((serviceClient) => {
      const blobClient = serviceClient.getContainerClient(containerName).getBlobClient(filename);
      blobClient.download().then(resp => {
        resp.blobBody.then(blob => {
          handler(blob);
        }).catch((error) => {
          console.log('An error happend when attempting to download.  Get a new SAS');
          this._sas = '';
          this.attempts++;
          if (this.attempts < this.numRetries) {
            this.downloadBlob(filename, handler, containerName);
          } else {
            this.attempts = 0;
            handler(null);
          }
        });
      }).catch((error) => {
        this._sas = '';
        this.attempts++;
        if (this.attempts < this.numRetries) {
          this.downloadBlob(filename, handler, containerName);
        } else {
          this.attempts = 0;
          handler(null);
        }
      });
    });
  }

  async deleteBlob(name: string, handler: () => void) {
    (await this.getServiceClient()).subscribe((serviceClient) => {
      serviceClient.getContainerClient(this.mainContainerName).deleteBlob(name).then(() => {
        handler();
      });
      /* const blobClient = serviceClient.getContainerClient(this.mainContainerName).getBlobClient(name);
       blobClient.delete().then(() => {
         handler();
       });*/
    });
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // This will need to be adapted to only get a SAS if there is not one, or if the current one is expired.  This will reduce the risk of
  // hitting any request limits there may likely be.
  private getSass(): Observable<string> {
    return new Observable((obs) => {
      this.getSas().subscribe((res) => {
        obs.next(res);
        obs.complete();
      });
    });
  }

  private async getServiceClient(): Promise<Observable<BlobServiceClient>> {
    let blobServiceClient;
    const promises: any[] = [
      this.getAccountName().toPromise(),
      this.getSass().toPromise()
    ];

    try {
      const results: any[] = await Promise.all(promises);
      if (results[0]) {
        this.accountName = results[0];
      }
      if (results[1]) {
        this._sas = results[1];
      }
      blobServiceClient = new Observable<BlobServiceClient>((obs) => {
        // Need to retrieve a new SAS;
        const sc = new BlobServiceClient(`https://${this.accountName}.blob.core.windows.net?${this._sas}`);
        obs.next(sc);
        obs.complete();
      });
    } catch (err) {
      console.log('Error retrieving account name and sas from the database!!!');
    }
    return blobServiceClient;
  }

  cancelUpload() {
    for (const abortController of this.abortControllers) {
      abortController.abort();
    }

    this.abortControllers = [];
  }
}
